import React from "react";
import { useTranslation } from "react-i18next";
import "./TelealarmeISSIntegration.css";

/**
 * Téléalarme ISS — 05 / Intégration & suivi.
 * Contenu FR/EN autonome pour préserver les fichiers i18n existants.
 * Présentation informative : aucune compatibilité ou conformité n'est présumée.
 */
const COPY = {
  fr: {
    eyebrow: "INSTALLATION & SUIVI",
    title: "Une intégration pensée pour les professionnels.",
    intro: "Une configuration adaptée commence par la compréhension de l'ascenseur existant.",
    steps: [
      {
        number: "01",
        title: "Analyse de l'existant",
        description: "Examen de l'équipement, des interfaces disponibles et des contraintes du site."
      },
      {
        number: "02",
        title: "Choix de la configuration",
        description: "Définition des composants, du mode de communication et des options de supervision."
      },
      {
        number: "03",
        title: "Mise en service & vérification",
        description: "Tests de communication et contrôle du fonctionnement prévu pour le site."
      }
    ],
    button: "Vérifier mon installation",
    panelTitle: "Les points à vérifier en amont",
    checklist: [
      "Compatibilité des interfaces",
      "Couverture et architecture réseau",
      "Alimentation et moyens de secours",
      "Accès aux alertes et événements selon options"
    ],
    disclaimer: "Liste indicative pour préparer le projet. Elle ne vaut ni audit sur site ni confirmation de conformité."
  },
  en: {
    eyebrow: "INSTALLATION & MONITORING",
    title: "Integration designed for professionals.",
    intro: "The right configuration starts with understanding the existing elevator installation.",
    steps: [
      {
        number: "01",
        title: "Assess the existing installation",
        description: "Review the equipment, available interfaces and on-site constraints."
      },
      {
        number: "02",
        title: "Choose the configuration",
        description: "Define the components, communication mode and optional monitoring functions."
      },
      {
        number: "03",
        title: "Commission and verify",
        description: "Carry out communication tests and check the operation intended for the site."
      }
    ],
    button: "Check my installation",
    panelTitle: "Points to check in advance",
    checklist: [
      "Interface compatibility",
      "Network coverage and architecture",
      "Power supply and backup arrangements",
      "Access to alerts and events, depending on options"
    ],
    disclaimer: "An indicative checklist to help prepare the project. It does not constitute an on-site audit or a confirmation of compliance."
  }
};

function ChecklistIcon() {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false">
      <path d="M6 8h6M16 8h10M6 16h6M16 16h10M6 24h6M16 24h10"
        stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="m7 8 1.5 1.5L11 6.7M7 16l1.5 1.5 2.5-2.8M7 24l1.5 1.5 2.5-2.8"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function TelealarmeISSIntegration() {
  const { i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0].toLowerCase();
  const copy = COPY[locale] || COPY.fr;

  return (
    <section
      id="iss-integration"
      className="iss-integration"
      aria-labelledby="iss-integration-title"
    >
      <div className="iss-integration__container">
        <header className="iss-integration__header">
          <p className="iss-integration__eyebrow">{copy.eyebrow}</p>
          <h2 id="iss-integration-title" className="iss-integration__title">
            {copy.title}
          </h2>
          <p className="iss-integration__intro">{copy.intro}</p>
        </header>

        <div className="iss-integration__layout">
          <div className="iss-integration__process">
            <ol className="iss-integration__steps">
              {copy.steps.map((step) => (
                <li className="iss-integration__step" key={step.number}>
                  <span className="iss-integration__stepNumber" aria-hidden="true">
                    {step.number}
                  </span>
                  <div className="iss-integration__stepContent">
                    <h3 className="iss-integration__stepTitle">{step.title}</h3>
                    <p className="iss-integration__stepDescription">
                      {step.description}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            <a className="iss-integration__cta" href="/contact">
              <span>{copy.button}</span>
              <ArrowIcon />
            </a>
          </div>

          <aside className="iss-integration__panel" aria-label={copy.panelTitle}>
            <div className="iss-integration__panelHeader">
              <span className="iss-integration__panelIcon"><ChecklistIcon /></span>
              <h3 className="iss-integration__panelTitle">{copy.panelTitle}</h3>
            </div>
            <ul className="iss-integration__checklist">
              {copy.checklist.map((item) => (
                <li className="iss-integration__checkItem" key={item}>
                  <span className="iss-integration__check" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" focusable="false">
                      <path d="m5 12 4.5 4.5L19 7" stroke="currentColor"
                        strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="iss-integration__disclaimer">{copy.disclaimer}</p>
          </aside>
        </div>
      </div>
    </section>
  );
}
